import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, type Paginated } from '@/lib/api';

export interface Address {
  id: number;
  label: string;
  street: string | null;
  city: string | null;
  country: string | null;
  notes: string | null;
}

export interface StorageLocation {
  id: number;
  address_id: number;
  parent_id: number | null;
  type: 'container' | 'shelf' | 'position' | 'custom';
  type_label: string;
  label: string;
  notes: string | null;
  display_path: string;
}

// =================== ADDRESSES ===================

export function useAddresses() {
  return useQuery({
    queryKey: ['addresses'],
    queryFn: async () => {
      const { data } = await api.get<Paginated<Address>>('/addresses', { params: { per_page: 200 } });
      return data.data;
    },
  });
}

export function useSaveAddress() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id?: number; payload: Partial<Address> }) => {
      if (id) {
        const { data } = await api.put<{ data: Address }>(`/addresses/${id}`, payload);
        return data.data;
      }
      const { data } = await api.post<{ data: Address }>('/addresses', payload);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['addresses'] }),
  });
}

export function useDeleteAddress() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/addresses/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['addresses'] });
      qc.invalidateQueries({ queryKey: ['storage-locations'] });
    },
  });
}

// =================== STORAGE LOCATIONS ===================

export function useStorageLocations(params: { address_id?: number } = {}) {
  return useQuery({
    queryKey: ['storage-locations', params],
    queryFn: async () => {
      const { data } = await api.get<Paginated<StorageLocation>>('/storage-locations', {
        params: { ...params, per_page: 500 },
      });
      return data.data;
    },
  });
}

export function useSaveStorageLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id, payload,
    }: { id?: number; payload: Partial<StorageLocation> }) => {
      if (id) {
        const { data } = await api.put<{ data: StorageLocation }>(`/storage-locations/${id}`, payload);
        return data.data;
      }
      const { data } = await api.post<{ data: StorageLocation }>('/storage-locations', payload);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['storage-locations'] }),
  });
}

export function useDeleteStorageLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/storage-locations/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['storage-locations'] }),
  });
}
